import React, { useState, useCallback, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import AccessBadge from '../components/access/AccessBadge';
import { SubscriptionTier } from '../../shared/types/access';
import FeatureLock from '../components/FeatureLock';
import { hasFeatureAccess, FEATURE_ACCESS_MAP } from '../data/featureAccess';
import AnimatedLoadingState from '../components/AnimatedLoadingState';
import ErrorState from '../components/ErrorState';

const API_URL = import.meta.env.VITE_API_URL || '';

// ============================================================
// Типы
// ============================================================

type AnalysisType =
  | 'talents'
  | 'karma'
  | 'destiny'
  | 'shadow'
  | 'potentials'
  | 'fate-collapse'
  | 'obstacle-point'
  | 'genogram';

interface AnalysisResult {
  type: AnalysisType;
  title: string;
  description: string;
  data: Record<string, unknown>;
  factors: string[];
  score?: number;
}

interface AllAnalysesResponse {
  analyses: AnalysisResult[];
  ready: boolean;
}

interface AIInterpretation {
  interpretation: string;
  recommendations: string[];
}

// ============================================================
// Данные анализов
// ============================================================

const ANALYSIS_META: Record<
  AnalysisType,
  { icon: string; title: string; description: string; color: string; featureId: string }
> = {
  talents: {
    icon: '💎',
    title: 'Таланты',
    description: 'Скрытые способности и природные ресурсы, заложенные в карте',
    color: '#7dd3c0',
    featureId: 'numerology_basic',
  },
  karma: {
    icon: '🔮',
    title: 'Карма',
    description: 'Кармические задачи, узлы и уроки из прошлых жизней',
    color: '#a78bfa',
    featureId: 'numerology_full',
  },
  destiny: {
    icon: '⭐',
    title: 'Предназначение',
    description: 'Ваша миссия и путь, заложенный в момент рождения',
    color: '#fbbf24',
    featureId: 'numerology_full',
  },
  shadow: {
    icon: '🌑',
    title: 'Тень',
    description: 'Скрытые стороны личности и подавленные качества',
    color: '#6b7280',
    featureId: 'natal-chart',
  },
  potentials: {
    icon: '🚀',
    title: 'Потенциалы',
    description: 'Нераскрытые возможности и точки роста',
    color: '#34d399',
    featureId: 'numerology_full',
  },
  'fate-collapse': {
    icon: '⚡',
    title: 'Крах судьбы',
    description: 'Критические точки жизненного пути и моменты трансформации',
    color: '#f87171',
    featureId: 'transits-advanced',
  },
  'obstacle-point': {
    icon: '🧱',
    title: 'Точка препятствий',
    description: 'Главные барьеры и способы их преодоления',
    color: '#fb923c',
    featureId: 'transits-advanced',
  },
  genogram: {
    icon: '🌳',
    title: 'Генограмма',
    description: 'Родовые паттерны и наследственные влияния',
    color: '#60a5fa',
    featureId: 'child_profiles',
  },
};

const ANALYSIS_ORDER: AnalysisType[] = [
  'talents',
  'karma',
  'destiny',
  'shadow',
  'potentials',
  'fate-collapse',
  'obstacle-point',
  'genogram',
];

// ============================================================
// Стили
// ============================================================

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)',
  padding: '32px 20px',
  position: 'relative',
};

const containerStyle: React.CSSProperties = {
  maxWidth: '1100px',
  margin: '0 auto',
};

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(32px, 5vw, 48px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: '12px',
  textAlign: 'center' as const,
  animation: 'goldShimmer 3s ease-in-out infinite',
};

const subtitleStyle: React.CSSProperties = {
  color: '#b0b0b0',
  fontSize: '16px',
  textAlign: 'center' as const,
  marginBottom: '32px',
};

const btnPrimaryStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
  border: 'none',
  padding: '12px 28px',
  borderRadius: '25px',
  color: '#0a0a1a',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const btnSmallStyle: React.CSSProperties = {
  background: 'rgba(212, 175, 55, 0.1)',
  border: '1px solid rgba(212, 175, 55, 0.3)',
  color: '#d4af37',
  padding: '8px 16px',
  borderRadius: '16px',
  fontSize: '13px',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

// ============================================================
// Компонент
// ============================================================

const SpecialAnalysesPage: React.FC = () => {
  const { user } = useAuth();
  const [userTier, setUserTier] = useState<string>('free');
  const [loadingTier, setLoadingTier] = useState(true);
  const [analyses, setAnalyses] = useState<Map<AnalysisType, AnalysisResult>>(new Map());
  const [loading, setLoading] = useState(false);
  const [loadingType, setLoadingType] = useState<AnalysisType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<AnalysisType | null>(null);
  const [interpretation, setInterpretation] = useState<AIInterpretation | null>(null);
  const [interpretationLoading, setInterpretationLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchTier = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payment/status`, {
          credentials: 'include', // HttpOnly cookies (фаза 1.1)
        });
        const data = await res.json();
        if (data.success && data.data.subscription) {
          setUserTier(data.data.subscription.tier || 'free');
        } else {
          setUserTier('free');
        }
      } catch {
        setUserTier('free');
      } finally {
        setLoadingTier(false);
      }
    };
    fetchTier();
  }, []);

  // Загрузить все анализы
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.get<AllAnalysesResponse>('/special-analyses/all');
    if (res.success && res.data?.analyses) {
      const map = new Map<AnalysisType, AnalysisResult>();
      res.data.analyses.forEach((a) => map.set(a.type, a));
      setAnalyses(map);
    } else {
      setError(res.error || 'Не удалось загрузить анализы');
    }
    setLoading(false);
  }, []);

  // Загрузить отдельный анализ
  const loadAnalysis = async (type: AnalysisType) => {
    setLoadingType(type);
    setError(null);
    const res = await api.get<AnalysisResult>(`/special-analyses/${type}`);
    if (res.success && res.data) {
      setAnalyses((prev) => {
        const next = new Map(prev);
        next.set(type, res.data as AnalysisResult);
        return next;
      });
    } else {
      setError(res.error || `Не удалось загрузить анализ: ${ANALYSIS_META[type].title}`);
    }
    setLoadingType(null);
  };

  // AI-интерпретация
  const loadInterpretation = async (type: AnalysisType) => {
    setInterpretationLoading(true);
    setInterpretation(null);
    const res = await api.get<AIInterpretation>(`/special-analyses/${type}/interpretation`);
    if (res.success && res.data) {
      setInterpretation(res.data);
    }
    setInterpretationLoading(false);
  };

  // Клик на карточку
  const handleCardClick = async (type: AnalysisType) => {
    setSelectedType(type);
    setInterpretation(null);

    if (!analyses.has(type)) {
      await loadAnalysis(type);
    }
  };

  const selectedAnalysis = selectedType ? analyses.get(selectedType) : null;

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
        {/* Заголовок */}
        <h1 style={{...titleStyle, fontSize: isMobile ? 'clamp(24px, 5vw, 36px)' : titleStyle.fontSize}}>Специальные анализы</h1>
        <p style={subtitleStyle}>
          Углублённые астрологические анализы для глубокого понимания себя
        </p>

        {error && <ErrorState message={error} onRetry={loadAll} />}

        {/* Кнопка "Рассчитать всё" */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <button
            style={{
              ...btnPrimaryStyle,
              opacity: loading ? 0.7 : 1,
            }}
            onClick={loadAll}
            disabled={loading}
          >
            {loading ? 'Загрузка...' : 'Рассчитать всё'}
          </button>
        </div>

        {/* Сетка карточек */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '16px',
            marginBottom: '32px',
          }}
        >
          {ANALYSIS_ORDER.map((type) => {
            const meta = ANALYSIS_META[type];
            const hasData = analyses.has(type);
            const isLoading = loadingType === type;
            const hasAccess = hasFeatureAccess(userTier, meta.featureId);
            const requiredTier = FEATURE_ACCESS_MAP[meta.featureId]?.requiredTier || 'premium';

            return (
              <div
                key={type}
                style={{
                  background: 'rgba(13, 20, 40, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(212, 175, 55, 0.12)',
                  borderRadius: '24px',
                  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
                  padding: isMobile ? '20px 16px' : '24px',
                  cursor: hasAccess ? 'pointer' : 'default',
                  transition: 'all 0.3s',
                  textAlign: 'center',
                  opacity: hasAccess ? 1 : 0.6,
                  position: 'relative',
                }}
                onClick={() => {
                  if (hasAccess) {
                    handleCardClick(type);
                  }
                }}
                onMouseEnter={(e) => {
                  if (hasAccess) {
                    e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.35)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 12px 48px rgba(212, 175, 55, 0.15), inset 0 0 40px rgba(13, 20, 40, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.12)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)';
                }}
              >
                {/* AccessBadge вместо lock icon */}
                {!hasAccess && (
                  <div style={{ marginBottom: '8px' }}>
                    <AccessBadge
                      featureId={meta.featureId}
                      userTier={userTier as SubscriptionTier}
                      requiredTier={requiredTier as SubscriptionTier}
                      featureName={meta.title}
                      size="sm"
                    />
                  </div>
                )}
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>{meta.icon}</div>
                <h3
                  style={{
                    color: hasAccess ? '#f0f0f0' : '#888',
                    fontSize: isMobile ? '15px' : '17px',
                    fontFamily: 'var(--font-display)',
                    fontStyle: 'italic',
                    marginBottom: '8px',
                  }}
                >
                  {meta.title}
                </h3>
                <p style={{ color: '#b0b0b0', fontSize: '13px', lineHeight: 1.5, marginBottom: '12px' }}>
                  {meta.description}
                </p>

                {isLoading && <AnimatedLoadingState />}

                {hasAccess && hasData && !isLoading && (
                  <span
                    style={{
                      display: 'inline-block',
                      background: `${meta.color}22`,
                      color: meta.color,
                      fontSize: '11px',
                      padding: '3px 10px',
                      borderRadius: '12px',
                    }}
                  >
                    Рассчитано
                  </span>
                )}

                {hasAccess && !hasData && !isLoading && (
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#666',
                      fontSize: '12px',
                    }}
                  >
                    Загрузить
                  </span>
                )}

                {!hasAccess && !isLoading && (
                  <FeatureLock
                    requiredTier={requiredTier as 'base' | 'premium' | 'pro'}
                    featureName={meta.title}
                    size="small"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Модальное окно с детальным результатом */}
        {selectedType && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px',
            }}
            onClick={() => {
              setSelectedType(null);
              setInterpretation(null);
            }}
          >
            <div
              style={{
                background: 'rgba(13, 20, 40, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(212, 175, 55, 0.15)',
                borderRadius: '24px',
                boxShadow: '0 8px 40px rgba(212, 175, 55, 0.1), inset 0 0 40px rgba(13, 20, 40, 0.5)',
                padding: isMobile ? '24px 16px' : '32px',
                width: '100%',
                maxWidth: '650px',
                maxHeight: '85vh',
                overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Заголовок модалки */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '24px',
                }}
              >
                <div>
                  <h2
                    style={{
                      color: '#f0f0f0',
                      fontSize: isMobile ? '20px' : '24px',
                      fontFamily: 'var(--font-display)',
                      fontStyle: 'italic',
                      margin: '0 0 8px',
                    }}
                  >
                    {ANALYSIS_META[selectedType].icon} {ANALYSIS_META[selectedType].title}
                  </h2>
                  <p style={{ color: '#b0b0b0', fontSize: '14px', margin: 0 }}>
                    {ANALYSIS_META[selectedType].description}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedType(null);
                    setInterpretation(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0 4px',
                  }}
                >
                  ✕
                </button>
              </div>

              {loadingType === selectedType && <AnimatedLoadingState />}

              {selectedAnalysis && (
                <>
                  {/* Score */}
                  {selectedAnalysis.score !== undefined && (
                    <div
                      style={{
                        background: 'rgba(212, 175, 55, 0.06)',
                        border: '1px solid rgba(212, 175, 55, 0.15)',
                        borderRadius: '16px',
                        padding: '16px',
                        marginBottom: '20px',
                        textAlign: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '36px',
                          fontWeight: 'bold',
                          color: '#d4af37',
                          fontFamily: 'var(--font-display)',
                        }}
                      >
                        {selectedAnalysis.score}
                      </span>
                      <span style={{ color: '#888', fontSize: '14px', marginLeft: '8px' }}>/ 100</span>
                    </div>
                  )}

                  {/* Описание */}
                  <div
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(212, 175, 55, 0.12)',
                      borderRadius: '16px',
                      padding: isMobile ? '16px' : '20px',
                      marginBottom: '20px',
                    }}
                  >
                    <h3
                      style={{
                        color: '#d4af37',
                        fontSize: '16px',
                        marginBottom: '12px',
                        marginTop: 0,
                      }}
                    >
                      Описание
                    </h3>
                    <p
                      style={{
                        color: '#e0e0e0',
                        fontSize: '15px',
                        lineHeight: 1.8,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {selectedAnalysis.description}
                    </p>
                  </div>

                  {/* Факторы */}
                  {selectedAnalysis.factors.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h3
                        style={{
                          color: '#d4af37',
                          fontSize: '16px',
                          marginBottom: '12px',
                        }}
                      >
                        Ключевые факторы
                      </h3>
                      <ul
                        style={{
                          color: '#e0e0e0',
                          fontSize: '14px',
                          lineHeight: 1.8,
                          paddingLeft: '20px',
                          margin: 0,
                        }}
                      >
                        {selectedAnalysis.factors.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* AI-интерпретация */}
                  <div>
                    {!interpretationLoading && !interpretation && (
                      <button style={btnSmallStyle} onClick={() => loadInterpretation(selectedType)}>
                        ✨ Интерпретация
                      </button>
                    )}
                    {interpretationLoading && <AnimatedLoadingState />}
                    {interpretation && (
                      <div
                        style={{
                          background: 'rgba(212, 175, 55, 0.04)',
                          border: '1px solid rgba(212, 175, 55, 0.12)',
                          borderRadius: '16px',
                          padding: isMobile ? '16px' : '20px',
                          marginTop: '16px',
                        }}
                      >
                        <h3
                          style={{
                            color: '#d4af37',
                            fontSize: '16px',
                            marginBottom: '12px',
                            marginTop: 0,
                          }}
                        >
                          Интерпретация
                        </h3>
                        <p
                          style={{
                            color: '#e0e0e0',
                            fontSize: '15px',
                            lineHeight: 1.8,
                            margin: '0 0 16px',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {interpretation.interpretation}
                        </p>
                        {interpretation.recommendations.length > 0 && (
                          <div>
                            <h4 style={{ color: '#d4af37', fontSize: '14px', marginBottom: '8px' }}>
                              Рекомендации
                            </h4>
                            <ul
                              style={{
                                color: '#e0e0e0',
                                fontSize: '14px',
                                lineHeight: 1.8,
                                paddingLeft: '20px',
                                margin: 0,
                              }}
                            >
                              {interpretation.recommendations.map((r, i) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {!selectedAnalysis && loadingType !== selectedType && (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <button style={btnPrimaryStyle} onClick={() => loadAnalysis(selectedType)}>
                    Загрузить анализ
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialAnalysesPage;
